package co.uk.paxes.pinpoint.web.platform

import kotlin.js.JsExport
import kotlin.js.JsName

/**
 * Detects the runtime platform environment (Facebook Instant Games vs Standalone).
 * 
 * This component checks for the presence of the FBInstant global object to determine
 * if the game is running on Facebook Instant Games or as a standalone web application.
 */
@JsExport
object PlatformDetector {
    
    enum class Platform {
        FACEBOOK,
        STANDALONE
    }
    
    private var detectedPlatform: Platform? = null
    
    /**
     * Detect the current platform by checking for FBInstant global object.
     * Caches the result for subsequent calls.
     * 
     * @return Platform.FACEBOOK if FBInstant is defined, Platform.STANDALONE otherwise
     */
    @JsName("detectPlatform")
    fun detectPlatform(): Platform {
        if (detectedPlatform != null) {
            return detectedPlatform!!
        }
        
        val platform = if (isFBInstantAvailable()) {
            console.log("✅ PlatformDetector: Detected FACEBOOK platform")
            Platform.FACEBOOK
        } else {
            console.log("✅ PlatformDetector: Detected STANDALONE platform")
            Platform.STANDALONE
        }
        
        detectedPlatform = platform
        return platform
    }
    
    /**
     * Check if running on Facebook Instant Games.
     */
    @JsName("isFacebook")
    fun isFacebook(): Boolean {
        return detectPlatform() == Platform.FACEBOOK
    }
    
    /**
     * Check if running as standalone web application.
     */
    @JsName("isStandalone")
    fun isStandalone(): Boolean {
        return detectPlatform() == Platform.STANDALONE
    }
    
    /**
     * Check if FBInstant global object is available AND we're in Facebook context.
     * Facebook games always run in an iframe, so we check both conditions.
     */
    private fun isFBInstantAvailable(): Boolean {
        val hasFBInstant = js("typeof FBInstant !== 'undefined'") as Boolean
        val isInFacebookFrame = js("window.self !== window.top") as Boolean // Facebook games run in iframe
        return hasFBInstant && isInFacebookFrame
    }
    
    /**
     * Reset the cached platform detection (for testing).
     */
    @JsName("resetDetection")
    fun resetDetection() {
        detectedPlatform = null
        console.log("🔄 PlatformDetector: Reset detection cache")
    }
}
